import { useState, useEffect } from "react";
import { Box, Drawer, useMediaQuery } from "@mui/material";
import { NavItem } from "../ui/Navitem";
import { siderbarListItems } from "../assets/json/sidebar";
import { useAuthStore } from "../store";
import { useNavigate } from "react-router-dom";
import HeaderLogo from "../assets/header-logo.png";

const Sidebar = ({ open, onClose, ...props }) => {
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const permissions = useAuthStore((state) => state.permissions);
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [filteredItems, setFilteredItems] = useState([]);
  const navigate = useNavigate();

  // Load sidebar items initially and on user permission changes.
  // Subscribe to `permissions` (not only `hasPermission`): the latter is a stable
  // function reference, so the sidebar would not re-render after checkAuthStatus()
  // fills permissions. Child effects can also run before App's checkAuthStatus effect,
  // so the first pass often sees an empty permission list — without this, only items
  // without effective RBAC checks (e.g. Help, mis-filtered Report/Logs) stay visible.
  useEffect(() => {
    const filterSidebarItems = () => {
      const updatedItems = siderbarListItems()
        .map((item) => ({
          ...item,
          sub: item.sub?.filter(
            (subItem) =>
              !subItem.requiredRoles ||
              subItem.requiredRoles.some((role) => hasPermission(role))
          ),
        }))
        .filter((item) => {
          if (item.sub) return item.sub.length > 0;
          if (item.requiredRoles?.length) {
            return item.requiredRoles.some((role) => hasPermission(role));
          }
          return true;
        });
      setFilteredItems(updatedItems);

      navigate(
        window.location.pathname === "" ||
          window.location.pathname === "/dashboard"
          ? updatedItems[0].extendable
            ? `/${updatedItems[0].sub[0]?.href}`
            : `/${updatedItems[0].href}`
          : `${window.location.pathname}`
      );
      // Check if the active index is still valid after filtering
      if (activeIndex >= updatedItems.length) {
        setActiveIndex(-1);
      }
    };

    filterSidebarItems();
  }, [activeIndex, navigate, permissions, hasPermission]);

  const handleItemClick = (index) => {
    if (index === activeIndex) {
      // If clicking the same item, toggle it (collapse)
      setActiveIndex(-1);
    } else {
      // If clicking a different item, expand it
      setActiveIndex(index);
    }
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: "secondary.main",
          color: "secondary.contrastText",
          width: 260,
          border: "none",
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant={lgUp ? "permanent" : "temporary"}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <div>
          <Box sx={{ p: 4 }}>
            <Box
              sx={{
                alignItems: "center",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                borderRadius: 1,
                paddingBottom: 3,
              }}
            >
              <img src={HeaderLogo} alt="Logo" style={{ width: "40%" }} />
              {/* <Typography color="inherit" variant="caption">
                version 2.0
              </Typography> */}
            </Box>
          </Box>
        </div>
        <Box
          sx={{
            flexGrow: 1,
            maxHeight: "calc(100vh - 15px)",
            overflowY: "auto",
          }}
        >
          {filteredItems.map((item, index) => (
            <NavItem
              key={item.title}
              icon={item.icon}
              href={item.href}
              title={item.title}
              sub={item.sub}
              active={index === activeIndex}
              extendable={item.extendable}
              onClick={() => handleItemClick(index)}
            />
          ))}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;